import React from 'react';
import PropTypes from 'prop-types';
import { Column, List, Row } from '@pella/sash-framework';
import { classNames } from '@pella/sash-framework/dist/utilities';
import { CTA, Image } from '../../';

const CTARibbon = ({ data, className, ...other }) => {
	if (!data) return null;

	const ribbonClasses = classNames({
		'ctaRibbon relative my-8 xl:my-12 text-center lg:text-left p-5 xl:py-8 xl:px-12': true,
		'grid items-center sm:gap-x-7 2xl:gap-x-24 grid-cols-1fr md:grid-cols-2 grid-rows-[repeat(3,minmax(min-content,max-content)] md:grid-rows-[1fr_repeat(2,minmax(min-content,max-content)_1fr]': data.ribbon_style === 'right-text',
		'grid auto-rows-auto items-center sm:gap-x-7 2xl:gap-x-24 grid-cols-1fr md:grid-cols-[3fr_1fr]': data.ribbon_style === 'financing',
		'text-white [&_p_a]:text-white hover:[&_p_a]:no-underline': data.background_hex_value === '#004990' || data.background_hex_value === '#000000',
		'border border-gray-15': data.background_hex_value === '#ffffff',
		[className]: className,
	});

	return (
		<div
			className={ribbonClasses}
			style={{ backgroundColor: data.background_hex_value }}
			{...other}
		>
			{data.ribbon_style === 'right-text' && (
				<div className='ctaRibbon--rightText'>
					{data.heading && (
						<p
							className='ctaRibbon-heading text-h1 col-start-1 row-start-1 mb-1 font-semibold !leading-none md:row-start-2'
							dangerouslySetInnerHTML={{ __html: data.heading }}
						/>
					)}

					{data.disclaimer && (
						<div
							className='ctaRibbon-disclaimer col-start-1 row-start-2 m-0 text-xs md:row-start-3'
							dangerouslySetInnerHTML={{ __html: data.disclaimer }}
						/>
					)}

					{data.right_text && (
						<p
							className='ctaRibbon-rightText col-start-1 row-start-3 mb-0 mt-5 text-center text-xl font-semibold uppercase leading-tight tracking-3px md:col-start-2 md:row-span-full md:row-start-1 lg:mt-0 xl:text-3xl'
							dangerouslySetInnerHTML={{ __html: data.right_text }}
						/>
					)}
				</div>
			)}

			{data.ribbon_style === 'promo' && (
				<div className='ctaRibbon--promo'>
					<Row
						align='center'
						className='ctaRibbon-Row'
					>
						{data.icon?.url && (
							<Column
								xs={12}
								lg='auto'
							>
								<Image
									data={{ file: data.icon, image_alt: '' }}
									className='ctaRibbon-image mx-auto mb-4 !block w-[100px] lg:mb-0'
								/>
							</Column>
						)}

						<Column
							xs={12}
							lg='fluid'
						>
							{data.heading && (
								<p
									className='ctaRibbon-heading mb-1 text-2xl font-semibold !leading-none md:text-3xl xl:text-4xl'
									dangerouslySetInnerHTML={{ __html: data.heading }}
								/>
							)}
							{data.subheading && (
								<p
									className='ctaRibbon-subheading m-0 text-lg xl:text-xl'
									dangerouslySetInnerHTML={{ __html: data.subheading }}
								/>
							)}
							{data.disclaimer && (
								<div
									className='ctaRibbon-disclaimer m-0 text-xs'
									dangerouslySetInnerHTML={{ __html: data.disclaimer }}
								/>
							)}
						</Column>

						<Column
							xs={12}
							lg='auto'
						>
							{data.right_text && (
								<p
									className='ctaRibbon-rightText mb-0 mt-5 text-center text-xl font-semibold uppercase leading-tight tracking-3px lg:mt-0'
									dangerouslySetInnerHTML={{ __html: data.right_text }}
								/>
							)}

							{data.cta?.[0] && (
								<List
									bullets='none'
									className='ctaRibbon-btnList flex flex-row flex-wrap gap-2.5 text-right'
								>
									{data.cta.map((link, index) => (
										<li
											key={index}
											className='max-w-full flex-[1_0_auto]'
										>
											<CTA
												data={link}
												className='ctaRibbon-btn mt-5 !w-full lg:mt-0 lg:!w-auto'
											/>
										</li>
									))}
								</List>
							)}
						</Column>
					</Row>
				</div>
			)}

			{data.ribbon_style === 'financing' && (
				<div className='ctaRibbon--financing'>
					{data.heading && (
						<p
							className='ctaRibbon-heading col-start-1 mb-1 text-2xl font-bold !leading-none text-primary md:text-3xl xl:text-5xl'
							dangerouslySetInnerHTML={{ __html: data.heading }}
						/>
					)}

					{data.subheading && (
						<p
							className='ctaRibbon-subheading col-start-1 m-0 text-lg xl:text-xl'
							dangerouslySetInnerHTML={{ __html: data.subheading }}
						/>
					)}

					{data.cta?.[0] && (
						<List
							bullets='none'
							layout='fluid'
							className='ctaRibbon-btnList col-start-1 gap-6 md:col-start-2'
						>
							{data.cta.map((link, index) => (
								<li
									key={index}
									className='max-w-full'
								>
									<CTA
										data={link}
										className='ctaRibbon-btn mt-5 !w-full lg:mt-0 lg:!w-auto'
									/>
								</li>
							))}
						</List>
					)}
				</div>
			)}

			{(data.ribbon_style === 'cta' || !data.ribbon_style) && (
				<div className='ctaRibbon--default'>
					<Row
						align='center'
						className='ctaRibbon-Row'
					>
						<Column
							xs={12}
							lg='fluid'
						>
							{data.heading && (
								<p
									className='ctaRibbon-heading mb-4 text-2xl font-semibold !leading-none md:mb-1 md:text-3xl xl:text-4xl'
									dangerouslySetInnerHTML={{ __html: data.heading }}
								/>
							)}

							{data.subheading && (
								<p
									className='ctaRibbon-subheading m-0 text-lg xl:text-xl'
									dangerouslySetInnerHTML={{ __html: data.subheading }}
								/>
							)}
						</Column>

						<Column
							xs={12}
							lg='auto'
						>
							{data.cta?.[0] && (
								<List
									bullets='none'
									className='ctaRibbon-btnList mt-2.5 flex flex-row flex-wrap gap-2.5 lg:mt-0'
								>
									{data.cta.map((link, index) => (
										<li
											key={index}
											className='max-w-full flex-[1_0_auto]'
										>
											<CTA
												data={link}
												className='ctaRibbon-btn !w-full lg:!w-auto'
											/>
										</li>
									))}
								</List>
							)}
						</Column>
					</Row>
				</div>
			)}
		</div>
	);
};

CTARibbon.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string,
};

export default CTARibbon;
